import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import FacilityForm from '../components/FacilityForm';
import { DataTable, StatusBadge } from '../components/ui';

const FacilityListPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      const response = await api.get('/facilities/all');
      setFacilities(response.data.data);
      setError('');
    } catch (err) {
      setError(t('admin:facilities.errors.loadFailed'));
      console.error('Error fetching facilities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFacility = () => {
    setSelectedFacility(null);
    setFormMode('create');
    setOpenForm(true);
  };

  const handleEditFacility = (facility) => {
    setSelectedFacility(facility);
    setFormMode('edit');
    setOpenForm(true);
  };

  const handleDeleteFacility = (facility) => {
    setSelectedFacility(facility);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/facilities/${selectedFacility._id}`);
      setSuccess(t('admin:facilities.messages.deleteSuccess'));
      setOpenDeleteDialog(false);
      setSelectedFacility(null);
      fetchFacilities();
    } catch (err) {
      setError(t('admin:facilities.errors.deleteFailed'));
      console.error('Error deleting facility:', err);
    }
  };

  const handleFormSubmit = async (facilityData) => {
    try {
      if (formMode === 'create') {
        await api.post('/facilities', facilityData);
        setSuccess(t('admin:facilities.messages.createSuccess'));
      } else {
        await api.put(`/facilities/${selectedFacility._id}`, facilityData);
        setSuccess(t('admin:facilities.messages.updateSuccess'));
      }
      setOpenForm(false);
      setSelectedFacility(null);
      fetchFacilities();
    } catch (err) {
      setError(t('admin:facilities.errors.saveFailed'));
      console.error(`Error ${formMode}ing facility:`, err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatOperatingHours = (operatingHours) => {
    if (!operatingHours || operatingHours.length === 0) {
      return t('admin:facilities.notSet');
    }
    return t('admin:facilities.operatingHours.daysConfigured', { count: operatingHours.length });
  };

  // Define table columns
  const columns = [
    {
      key: 'name',
      title: t('common:labels.name'),
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {row.name}
          </div>
          <div style={{ color: '#666', fontSize: '0.875rem', maxWidth: '200px' }}>
            {row.description.length > 50
              ? `${row.description.substring(0, 50)}...`
              : row.description}
          </div>
        </div>
      )
    },
    {
      key: 'type',
      title: t('common:labels.type'),
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <StatusBadge status="info" variant="outline">
          {row.type}
        </StatusBadge>
      )
    },
    {
      key: 'capacity',
      title: t('common:labels.capacity'),
      sortable: true,
      render: (value, row) => (
        <div style={{ fontWeight: '500' }}>
          {t('admin:facilities.capacityPeople', { count: row.capacity })}
        </div>
      )
    },
    {
      key: 'status',
      title: t('common:labels.status'),
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <StatusBadge status={getStatusColor(row.status)}>
          {row.status}
        </StatusBadge>
      )
    },
    {
      key: 'operatingHours',
      title: t('admin:facilities.operatingHours.title'),
      render: (value, row) => (
        <div style={{ color: '#666' }}>
          {formatOperatingHours(row.operatingHours)}
        </div>
      )
    },
    {
      key: 'bookingConfig',
      title: t('admin:facilities.bookingConfig'),
      render: (value, row) => (
        <div>
          <div style={{ fontSize: '0.875rem' }}>
            {t('admin:facilities.slotDuration', { duration: row.bookingConfig?.slotDuration || 60 })}
          </div>
          <div style={{ color: '#666', fontSize: '0.75rem' }}>
            {t('admin:facilities.leadTime', { time: row.bookingConfig?.leadTime || 1 })}
          </div>
        </div>
      )
    },
    {
      key: 'isActive',
      title: t('common:labels.active'),
      sortable: true,
      render: (value, row) => (
        <StatusBadge status={row.isActive ? 'success' : 'default'}>
          {row.isActive ? t('common:states.active') : t('common:states.inactive')}
        </StatusBadge>
      )
    }
  ];

  // Define table actions
  const actions = {
    row: [
      {
        icon: <EditIcon />,
        title: t('admin:facilities.actions.edit'),
        onClick: handleEditFacility,
        variant: 'ghost'
      },
      {
        icon: <DeleteIcon />,
        title: t('admin:facilities.actions.delete'),
        onClick: handleDeleteFacility,
        variant: 'danger'
      }
    ]
  };

  // Define empty state
  const emptyState = (
    <div>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏢</div>
      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
        {t('admin:facilities.emptyState.title')}
      </div>
      <div style={{ color: '#666' }}>
        {t('admin:facilities.emptyState.description')}
      </div>
    </div>
  );

  if (!user || user.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('common:messages.error.accessDenied')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('admin:facilities.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateFacility}
        >
          {t('admin:facilities.actions.create')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Data Table */}
      <DataTable
        data={facilities}
        columns={columns}
        loading={loading}
        error={error}
        sorting={{ enabled: true }}
        filtering={{ enabled: true }}
        actions={actions}
        emptyState={emptyState}
      />

      {/* Facility Form Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {formMode === 'create' ? t('admin:facilities.createTitle') : t('admin:facilities.editTitle')}
        </DialogTitle>
        <DialogContent>
          <FacilityForm
            facility={selectedFacility}
            onSubmit={handleFormSubmit}
            onCancel={() => setOpenForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('common:buttons.delete')} - {t('common:buttons.confirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('admin:facilities.deleteConfirmation', { name: selectedFacility?.name })}
            {t('common:messages.cannotUndo')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common:buttons.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common:buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacilityListPage;